import {
  isNullOrUndefined,
  isNullOrUndefinedOrEmpty,
  DateRange
}                                from 'rewire-common';
import {
  IAuditLog,
  IResult,
  AuditLogFilterInput,
  AuditLogSortInput,
  AuditLogEntryType,
  AuditLogEntryOperation,
  FieldFilter,
  INameEntity,
}                                from '../DataTypes';
import DataQueries               from '../graphql/Queries';
import EntityListModel           from '../ui/EntityListModel';
import PrioritySortMenuModel, {
  IPrioritySortMenuItem
}                                from '../ui/PrioritySortMenuModel';
import auditLogFilterDialogModel from './AuditLogFilterDialogModel';
import { freeze } from 'rewire-core';

export interface IAuditLogFilters {
  ids?:            string[];
  effective?:      DateRange;
  types?:          AuditLogEntryType[];
  operations?:     AuditLogEntryOperation[];
  description?:    string;
  entityIds?:      string[];
  transactionIds?: string[];
  createdBy?:      INameEntity[];
  createdDate?:    DateRange;
  custom?:         FieldFilter[];
}

const auditLogListReturnSchema = `
  id
  operation
  createdBy
  createdDate
`;

interface IAuditLogListState {
  filters:                  IAuditLogFilters;
  builtInFilters:           IAuditLogFilters;
  entityDataFilters:        FieldFilter[];
  builtInEntityDataFilters: FieldFilter[];
}

export class AuditLogListModel extends EntityListModel<IAuditLogListState> {
  sortModel: PrioritySortMenuModel;

  constructor() {
    super();

    this.state.filters                  = {};
    this.state.builtInFilters           = {};
    this.state.entityDataFilters        = [];
    this.state.builtInEntityDataFilters = [];
    this.sortModel                      = new PrioritySortMenuModel(() => this.load(), this.getPrioritySortItemsFn());
  }

  getAuditLog(auditLogId: string): IAuditLog | undefined {
    return this.getEntity(auditLogId) as IAuditLog | undefined;
  }

  get auditLogs(): IAuditLog[] {
    return this.state.entities as IAuditLog[];
  }

  get selectedAuditLog(): IAuditLog | undefined {
    return this.state.selectedEntity as IAuditLog | undefined;
  }

  set selectedAuditLog(auditLog: IAuditLog | undefined) {
    this.state.selectedEntity = auditLog;
  }

  get auditLogCount(): number {
    return this.auditLogs.length;
  }

  get filters(): IAuditLogFilters {
    return this.state.filters;
  }

  set filters(filters: IAuditLogFilters) {
    this.state.filters = this.getCleanFilters(filters);
    this.load();
  }

  get builtInFilters(): IAuditLogFilters {
    return this.state.builtInFilters;
  }

  set builtInFilters(builtInFilters: IAuditLogFilters) {
    this.state.builtInFilters = this.getCleanFilters(builtInFilters);
  }

  get entityDataFilters(): FieldFilter[] {
    return this.state.entityDataFilters;
  }

  set entityDataFilters(entityDataFilters: FieldFilter[]) {
    this.state.entityDataFilters = entityDataFilters;
    this.load();
  }

  get builtInEntityDataFilters(): FieldFilter[] {
    return this.state.builtInEntityDataFilters;
  }

  set builtInEntityDataFilters(builtInEntityDataFilters: FieldFilter[]) {
    this.state.builtInEntityDataFilters = builtInEntityDataFilters;
  }

  setAllFilters(filters: IAuditLogFilters, entityDataFilters: FieldFilter[]) {
    freeze(() => {
      this.state.filters           = this.getCleanFilters(filters);
      this.state.entityDataFilters = entityDataFilters;
    });
    this.load();
  }

  getCleanFilters(filtersToClean: IAuditLogFilters) {
    const _cleanFilters = {} as IAuditLogFilters;
    for (const k of Object.keys(filtersToClean) as (keyof IAuditLogFilters)[]) {
      if (!isNullOrUndefined(filtersToClean[k]) && (k !== 'custom' || filtersToClean['custom']!.length)) {
        _cleanFilters[k] = filtersToClean[k] as any;
      }
    }

    return _cleanFilters;
  }

  get hasFilters(): boolean {
    const builtInFilterKeys           = Object.keys(this.builtInFilters);
    const builtInEntityDataFilterKeys = this.builtInEntityDataFilters.map(edf => edf.field);
    return (Object.keys(this.filters).some(k => !builtInFilterKeys.some(bk => bk === k)) ||
            this.entityDataFilters.some(edf => !builtInEntityDataFilterKeys.some(bk => bk === edf.field)));
  }

  get sorts(): AuditLogSortInput {
    return this.sortModel.sorts;
  }

  set sorts(sorts: AuditLogSortInput) {
    this.sortModel.sorts = sorts;
  }

  get defaultCreatedByName(): string {
    return 'System';
  }

  clearFilters() {
    this.setAllFilters({}, []);
  }

  async showFilterDialog() {
    const entityDataFilters = await this.getEntityDataFiltersForFilterDialog();
    auditLogFilterDialogModel.load({filters: this.filters, entityDataFilters: entityDataFilters});
  }

  // NOTE: client side entity data filters not fully supported. Only using for an "employees" filter currently. Fully supported on server.
  async getEntityDataFiltersForFilterDialog() {
    const employeesFilter = this.entityDataFilters.find(edf => edf.field === 'employeeId');
    const employeeIds     = [employeesFilter?.in ?? employeesFilter?.eq].flat().filter(e => !!e);
    if (employeeIds.length) {
      const employees = await DataQueries.getEmployeesByIds('id name code', employeeIds);
      return {employees: employees};
    }

    return {};
  }

  getPrioritySortItemsFn(): () => IPrioritySortMenuItem[] {
    return () => {
      return [
        {name: 'type',          title: 'Type'},
        {name: 'operation',     title: 'Operation'},
        {name: 'createdBy',     title: 'Created By'},
        {name: 'transactionId', title: 'Created Date'},
        {name: 'effective',     title: 'Effective'},
      ];
    };
  }

  get filtersForQuery(): AuditLogFilterInput {
    const filters      = Object.assign({}, this.filters, this.builtInFilters);
    const queryFilters = filters as AuditLogFilterInput;

    if (filters.createdBy) {
      queryFilters.createdBy = filters.createdBy.map(cb => cb.name);
    }
    return queryFilters;
  }

  get entityDataFiltersForQuery(): FieldFilter[] {
    return [...this.entityDataFilters.filter(edf => !this.builtInEntityDataFilters.some(bedf => bedf.field === edf.field)), ...this.builtInEntityDataFilters];
  }

  getEntityDisplayName(auditLog: IAuditLog) {
    return `${auditLog.createdDate.toDateString()} - ${auditLog.operation} by: ${!isNullOrUndefinedOrEmpty(auditLog.createdBy) && auditLog.createdBy !== '«unknown»' ? auditLog.createdBy : this.defaultCreatedByName}`;
  }

  _pagingLoad = async (): Promise<{ data?: IAuditLog[], cursor?: string, totalCount: number }> =>  {
    this.currentPage = this.pagingModel.page;

    const results = await DataQueries.getAuditLogs(auditLogListReturnSchema, this.filtersForQuery, this.entityDataFiltersForQuery, this.sorts, this.pagingModel.itemsPerPage, this.pagingModel.currentPage) as IResult<IAuditLog>;
    return results;
  };

  async loadItem(auditLogId: string): Promise<IAuditLog | undefined> {
    const filter: AuditLogFilterInput = { ids: [auditLogId] };
    const results = await DataQueries.getAuditLogs(auditLogListReturnSchema, filter) as IResult<IAuditLog>;
    return (results?.data?.length && results.data[0]) || undefined;
  }
}

const auditLogListModel = new AuditLogListModel();
export default auditLogListModel;
